import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/apiClient";
import { PATHS } from "@/constants/testIds";
import { BookOpen, Clock, Layers, Building2 } from "lucide-react";

export default function PathsPage() {
  const [paths, setPaths] = useState([]);
  const [cat, setCat] = useState("All");
  const [assigned, setAssigned] = useState({ slugs: new Set(), companyName: null });

  useEffect(() => {
    api.get("/paths").then((r) => setPaths(r.data));
    api.get("/companies/mine/assigned-paths")
      .then((r) => setAssigned({
        slugs: new Set(r.data.assigned_slugs || []),
        companyName: r.data.company_name,
      }))
      .catch(() => {});
  }, []);

  const categories = useMemo(() => ["All", ...Array.from(new Set(paths.map((p) => p.category)))], [paths]);

  // Sort so mandatory paths appear first when a company has assigned any
  const sorted = useMemo(() => {
    const arr = [...paths];
    arr.sort((a, b) => {
      const am = assigned.slugs.has(a.slug) ? 0 : 1;
      const bm = assigned.slugs.has(b.slug) ? 0 : 1;
      return am - bm;
    });
    return arr;
  }, [paths, assigned]);

  const filtered = cat === "All" ? sorted : sorted.filter((p) => p.category === cat);

  return (
    <div data-testid={PATHS.root} className="space-y-8 animate-fade-up">
      <div>
        <div className="label-mono text-primary">// learning paths</div>
        <h1 className="mt-2 font-display text-3xl font-black tracking-tighter sm:text-4xl">Pick a path.</h1>
        <p className="mt-1 text-sm text-muted-foreground">Every path is guided by your personal mentor. Lessons adapt to you as you learn.</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {categories.map((c) => (
          <button
            key={c}
            data-testid={PATHS.categoryFilter(c)}
            onClick={() => setCat(c)}
            className={`rounded-full border px-4 py-1.5 font-mono text-xs uppercase tracking-widest transition ${cat === c ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-primary/50"}`}
          >
            {c}
          </button>
        ))}
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {filtered.map((p) => {
          const isMandatory = assigned.slugs.has(p.slug);
          return (
            <Link
              key={p.slug}
              data-testid={PATHS.card(p.slug)}
              to={`/app/paths/${p.slug}`}
              className={`card-hover group relative rounded-2xl border ${isMandatory ? "border-accent/60 shadow-[0_0_28px_-10px_rgba(204,255,0,0.4)]" : "border-border"} bg-card p-6`}
            >
              {isMandatory && (
                <div
                  data-testid={`path-mandatory-ribbon-${p.slug}`}
                  className="absolute -top-2 -right-2 flex items-center gap-1 rounded-full border border-accent bg-accent px-2.5 py-1 font-mono text-[0.6rem] uppercase tracking-widest text-accent-foreground shadow-lg"
                  title={assigned.companyName ? `Assigned by ${assigned.companyName}` : "Mandatory"}
                >
                  <Building2 className="h-3 w-3" /> Mandatory
                </div>
              )}
              <div className="flex items-center justify-between">
                <div
                  className="grid h-11 w-11 place-items-center rounded-lg font-display text-lg font-black"
                  style={{ backgroundColor: `${p.color}22`, color: p.color, border: `1px solid ${p.color}55` }}
                >
                  {p.title[0]}
                </div>
                <div className="label-mono">{p.category}</div>
              </div>
              <h3 className="mt-4 font-display text-xl font-bold">{p.title}</h3>
              <p className="mt-2 line-clamp-3 text-sm text-muted-foreground">{p.description}</p>
              <div className="mt-5 flex items-center gap-4 text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><Layers className="h-3.5 w-3.5" /> {p.lessons?.length || 0} lessons</span>
                <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> ~{p.duration_hours}h</span>
                <span className="flex items-center gap-1"><BookOpen className="h-3.5 w-3.5" /> {p.level}</span>
              </div>
              {isMandatory && assigned.companyName && (
                <div className="mt-3 label-mono text-accent">Assigned by {assigned.companyName}</div>
              )}
            </Link>
          );
        })}
      </div>
    </div>
  );
}
